'use client';

import { useEffect, useRef, type ReactNode } from 'react';
import { ThemeProvider } from '@wrksz/themes';
import { useThemeMode } from '@terra/ui-design-system';
import { APP_SETTING_KEY } from '../../electron-shared/app-settings/app-settings-keys';
import type { ThemePreference } from '../../electron-shared/app-settings/app-settings-schema';
import { useSharedAppSetting } from '../../hooks/useSharedAppSetting';
import { resolveThemeSyncAction } from './theme-preference-sync';

function SharedThemePreferenceSync() {
  const { preference: localPreference, toggleMode } = useThemeMode();
  const {
    status,
    value: sharedPreference,
    save,
  } = useSharedAppSetting(APP_SETTING_KEY.THEME_PREFERENCE);
  const lastSyncedPreferenceRef = useRef<ThemePreference | null>(null);

  useEffect(() => {
    if (status !== 'ready') {
      return;
    }

    const action = resolveThemeSyncAction({
      sharedPreference,
      localPreference,
      lastSyncedPreference: lastSyncedPreferenceRef.current,
    });

    switch (action.type) {
      case 'none':
        return;
      case 'apply':
        lastSyncedPreferenceRef.current = action.preference;

        if (action.preference !== localPreference) {
          toggleMode(action.preference);
        }

        return;
      case 'save':
        lastSyncedPreferenceRef.current = action.preference;
        void save(action.preference);

        return;
      default: {
        const unhandled: never = action;

        throw new Error(`Unhandled theme sync action: ${String(unhandled)}`);
      }
    }
  }, [status, sharedPreference, localPreference, toggleMode, save]);

  return null;
}

export function HybridClientThemeProvider({
  children,
}: {
  children: ReactNode;
}) {
  return (
    <ThemeProvider
      attribute="class"
      enableSystem
      storage="hybrid"
      disableTransitionOnChange
    >
      <SharedThemePreferenceSync />
      {children}
    </ThemeProvider>
  );
}
